import { useRef, useState, useEffect, useLayoutEffect, useCallback, RefObject } from 'react';

const px = (value: string): number => parseFloat(value) || 0;

// Mirrors ResizeObserverEntry.contentRect so pre-paint and observed widths are the same box.
// clientWidth, not getBoundingClientRect: it already excludes the border and any scrollbar.
const contentBoxWidth = (el: HTMLElement): number => {
  const style = getComputedStyle(el);

  return el.clientWidth - px(style.paddingLeft) - px(style.paddingRight);
};

function useContainerBreakpoint<T extends HTMLElement>(
  breakpoint: number,
  mode: 'max' | 'min',
): [RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [matches, setMatches] = useState(false);

  const test = useCallback(
    (width: number) => (mode === 'max' ? width <= breakpoint : width >= breakpoint),
    [breakpoint, mode],
  );

  // The observer's first callback can land after paint, leaving a frame at the wrong breakpoint.
  useLayoutEffect(() => {
    const el = ref.current;

    if (!el) return;

    const width = contentBoxWidth(el);

    // Zero means not laid out yet (or hidden), not narrow.
    if (width > 0) setMatches(test(width));
  }, [test]);

  useEffect(() => {
    const el = ref.current;

    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      setMatches(test(entries[0]?.contentRect.width ?? 0));
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, [test]);

  return [ref, matches];
}

/**
 * Drop-in replacement for useMediaQuery that watches a container element's width
 * instead of the viewport. Returns a ref to attach to the container and a boolean
 * indicating whether the query matches.
 *
 * @param maxWidth - The breakpoint in pixels. Returns true when the container is at or below this width.
 * @returns [ref, isCompact] - Attach ref to the container element.
 */
export function useContainerQuery<T extends HTMLElement = HTMLDivElement>(
  maxWidth: number,
): [RefObject<T | null>, boolean] {
  return useContainerBreakpoint<T>(maxWidth, 'max');
}

/**
 * `useContainerQuery` for the other end of the breakpoint, matching `@container (min-width: …)`.
 *
 * @param minWidth - The breakpoint in pixels. Returns true when the container is at or above this width.
 * @returns [ref, isWide] - Attach ref to the container element.
 */
export function useContainerMinWidth<T extends HTMLElement = HTMLDivElement>(
  minWidth: number,
): [RefObject<T | null>, boolean] {
  return useContainerBreakpoint<T>(minWidth, 'min');
}
